// Design agent: MasqIP wants to teach subnetting, and the CIDR <-> mask <-> wildcard mapping is the
// single table every learner ends up memorising or looking up constantly. Rather than making them
// leave the app, this view surfaces the full /0 -> /32 table with the numbers that matter for
// planning (total addresses, usable hosts) and a one-click way to copy any mask.
// Developer agent: Purely derived from utils/ipMath (getAllSubnetMasks, getTotalAddressCount,
// getUsableHostCount) — no local subnet arithmetic is duplicated here.
import { useState } from 'react';
import { getAllSubnetMasks, getTotalAddressCount, getUsableHostCount } from '../utils/ipMath';
import './ReferenceTable.css';

// Design agent: Copies the given mask to the clipboard when the browser exposes the Clipboard API,
// and otherwise fails silently — the row is still fully readable without the shortcut working.
// Developer agent: Mirrors the same guarded pattern used by BinaryBreakdown's copy buttons.
const copyMaskToClipboard = async (mask) => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(mask);
    }
  } catch (error) {
    // Design agent: Clipboard permission can be denied or the context can be insecure (http); the
    // reference table remains useful purely as a lookup either way.
  }
};

// Design agent: Renders the complete CIDR / mask / wildcard / address-count reference table,
// ascending from /0 (one giant network) to /32 (a single host), matching how the spec describes it.
// Developer agent: getAllSubnetMasks() already returns every valid mask descending from /32 to /0;
// this component simply reverses that list for display rather than re-deriving the data.
function ReferenceTable({ activePrefix }) {
  // Design agent: Tracks which row was most recently copied so the button can show brief
  // "Copied!" feedback instead of leaving the user unsure whether the click registered.
  const [recentlyCopiedCidr, setRecentlyCopiedCidr] = useState(null);

  const rows = [...getAllSubnetMasks()].reverse();

  // Design agent: A row acts as one big clickable/keyboard-focusable control (copy the mask),
  // exactly like clicking anywhere on a physical index card.
  const handleRowActivate = (row) => {
    copyMaskToClipboard(row.mask);
    setRecentlyCopiedCidr(row.cidr);
    // Design agent: The "Copied!" hint should not linger forever; it fades back to the mask value
    // after a moment so the table keeps reading as a static reference.
    setTimeout(() => {
      setRecentlyCopiedCidr((current) => (current === row.cidr ? null : current));
    }, 1500);
  };

  // Developer agent: Keyboard activation (Enter/Space) mirrors a native <button> since the row
  // itself is a div acting as a button for layout reasons (CSS grid columns are easier to align
  // across a header + many rows as plain divs than as a native <table>).
  const handleRowKeyDown = (event, row) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowActivate(row);
    }
  };

  return (
    <div className="reference-table-container" id="reference-table-container">
      <p className="reference-table-hint" id="reference-table-hint">
        Click any row to copy its dotted mask. The row matching the address you are currently
        inspecting is highlighted.
      </p>
      <div className="reference-table-wrapper" id="reference-table-wrapper">
        <div className="reference-table-header" id="reference-table-header">
          <span id="reference-table-header-cidr">CIDR</span>
          <span id="reference-table-header-mask">Mask</span>
          <span id="reference-table-header-wildcard">Wildcard</span>
          <span id="reference-table-header-total">Total addresses</span>
          <span id="reference-table-header-usable">Usable hosts</span>
        </div>
        <div className="reference-table-body" id="reference-table-body">
          {rows.map((row) => {
            const isActivePrefix = activePrefix === row.cidr;
            const totalAddresses = getTotalAddressCount(row.cidr);
            const usableHosts = getUsableHostCount(row.cidr);
            const isRecentlyCopied = recentlyCopiedCidr === row.cidr;
            return (
              <div
                key={row.cidr}
                className={`reference-table-row${isActivePrefix ? ' reference-table-row--active' : ''}`}
                id={`reference-table-row-${row.cidr}`}
                role="button"
                tabIndex={0}
                aria-label={`Copy mask for prefix /${row.cidr}: ${row.mask}`}
                onClick={() => handleRowActivate(row)}
                onKeyDown={(event) => handleRowKeyDown(event, row)}
              >
                <span className="reference-table-cell" id={`reference-table-row-${row.cidr}-cidr`}>
                  /{row.cidr}
                </span>
                <span className="reference-table-cell" id={`reference-table-row-${row.cidr}-mask`}>
                  {isRecentlyCopied ? 'Copied!' : row.mask}
                </span>
                <span className="reference-table-cell" id={`reference-table-row-${row.cidr}-wildcard`}>
                  {row.wildcard}
                </span>
                <span className="reference-table-cell" id={`reference-table-row-${row.cidr}-total`}>
                  {totalAddresses.toLocaleString()}
                </span>
                <span className="reference-table-cell" id={`reference-table-row-${row.cidr}-usable`}>
                  {usableHosts.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ReferenceTable;
